import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Alert } from '../../components/atoms/Alert'
import { Badge } from '../../components/atoms/Badge'
import { Button } from '../../components/atoms/Button'
import { Price } from '../../components/atoms/Price'
import { Spinner } from '../../components/atoms/Spinner'
import { RequestReturnModal } from '../../components/orders/RequestReturnModal'
import { ROUTES } from '../../../constants/routes.constants'
import {
  ORDERSTATUS,
  ORDERSTATUSLABEL,
  isReturnBranch,
  type OrderStatus
} from '../../../constants/order-status.constants'
import { useAccountOrderDetail } from '../../../viewmodels/account/useAccountOrderDetail'
import { Reveal, Stagger, StaggerItem } from '../../motion'

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

/** `/account/orders/:id` — line items, totals, shipping, tracking,
 *  and the appended order_status_history timeline + Return form
 *  (Kiran 2026-06-17). */
export function AccountOrderDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const { order, isLoading, error, notFound, fileReturn, cancelReturn, cancelOrder, reload } = useAccountOrderDetail(id)
  const [returnOpen, setReturnOpen] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [cancellingOrder, setCancellingOrder] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }
  if (notFound) {
    return (
      <>
        <h1 className="font-heading text-h2 text-charcoal mb-4">Order not found</h1>
        <p className="font-body text-body text-text-secondary mb-6">
          We couldn&apos;t find order <code className="font-mono text-body-sm">{id}</code>{' '}
          in your account.
        </p>
        <Link to={ROUTES.ACCOUNTORDERS}>
          <Button variant="primary">Back to orders</Button>
        </Link>
      </>
    )
  }
  if (error !== null || order === null) {
    return <Alert tone="error">{error ?? 'Could not load order'}</Alert>
  }

  return (
    <>
      <Helmet>
        <title>Order {order.orderNumber} — Uyyamadilu</title>
      </Helmet>
      <header className="mb-6">
        <Link
          to={ROUTES.ACCOUNTORDERS}
          className="font-body text-body-sm text-text-secondary hover:text-forest"
        >
          ← Back to orders
        </Link>
        <h1 className="mt-2 font-heading text-h1 text-charcoal">Order {order.orderNumber}</h1>
        <p className="mt-1 font-body text-body-sm text-text-secondary">
          Placed {formatDate(order.placedAt)}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge
            tone={isReturnBranch(order.currentStatus as OrderStatus) ? 'warning' : 'success'}
          >
            {ORDERSTATUSLABEL[order.currentStatus as OrderStatus] ?? order.currentStatus}
          </Badge>
          {order.returnable ? (
            <Button size="sm" variant="secondary" onClick={() => setReturnOpen(true)}>
              Request return
            </Button>
          ) : null}
          {order.currentStatus === ORDERSTATUS.RETURNPENDING ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={cancelling}
              onClick={async () => {
                setCancelError(null)
                setCancelling(true)
                const res = await cancelReturn()
                setCancelling(false)
                if (res.ok) reload()
                else setCancelError(res.message)
              }}
            >
              {cancelling ? 'Cancelling…' : 'Cancel return'}
            </Button>
          ) : null}
          {order.cancellable ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={cancellingOrder}
              onClick={async () => {
                setCancelError(null)
                setCancellingOrder(true)
                const res = await cancelOrder()
                setCancellingOrder(false)
                if (res.ok) reload()
                else setCancelError(res.message)
              }}
            >
              {cancellingOrder ? 'Cancelling…' : 'Cancel order'}
            </Button>
          ) : null}
        </div>
        {cancelError !== null ? (
          <div className="mt-3">
            <Alert tone="error">{cancelError}</Alert>
          </div>
        ) : null}
      </header>

      {order.statusTimeline.length > 0 ? (
        <Reveal as="section" className="mb-8 rounded-xl border border-border p-5 bg-bg-primary">
          <h2 className="font-heading text-h3 text-charcoal mb-3">Order status</h2>
          <ol className="space-y-2">
            {order.statusTimeline.map((entry, idx) => {
              const isLatest = idx === 0
              return (
                <li key={`${entry.status}-${entry.occurredAt}`} className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className={`mt-2 h-2 w-2 flex-shrink-0 rounded-full ${
                      isLatest ? 'bg-forest' : 'bg-text-secondary/40'
                    }`}
                  />
                  <div className="flex-1">
                    <p className={`font-body text-body ${isLatest ? 'text-charcoal font-semibold' : 'text-charcoal'}`}>
                      {ORDERSTATUSLABEL[entry.status as OrderStatus] ?? entry.status}
                    </p>
                    <p className="font-body text-body-xs text-text-secondary">
                      {formatDate(entry.occurredAt)}
                    </p>
                    {entry.notes !== null ? (
                      <p className="mt-1 font-body text-body-sm text-text-secondary whitespace-pre-wrap">
                        {entry.notes}
                      </p>
                    ) : null}
                  </div>
                </li>
              )
            })}
          </ol>
        </Reveal>
      ) : null}

      <Stagger as="section" className="space-y-3 mb-8" amount={0.05}>
        {order.lineItems.map((li) => (
          <StaggerItem
            key={li.id}
            className="flex items-start justify-between gap-4 rounded-xl border border-border p-4 bg-bg-primary"
          >
            <div>
              <p className="font-heading text-h3 text-charcoal">{li.title}</p>
              {li.variantTitle !== null ? (
                <p className="font-body text-body-sm text-text-secondary">{li.variantTitle}</p>
              ) : null}
              <p className="font-body text-body-sm text-text-secondary">
                Qty {li.quantity}
                {li.sku !== null ? <> · SKU {li.sku}</> : null}
              </p>
            </div>
            <Price amount={li.totalPrice} currency={li.currency} size="md" />
          </StaggerItem>
        ))}
      </Stagger>

      <Reveal as="section" className="rounded-xl border border-border p-5 bg-bg-primary max-w-md ml-auto space-y-2">
        <div className="flex justify-between font-body text-body">
          <span className="text-text-secondary">Subtotal</span>
          <Price amount={order.subtotal} currency={order.currency} />
        </div>
        <div className="flex justify-between font-body text-body">
          <span className="text-text-secondary">Shipping</span>
          <Price amount={order.shipping} currency={order.currency} />
        </div>
        <div className="flex justify-between font-body text-body">
          <span className="text-text-secondary">Tax</span>
          <Price amount={order.tax} currency={order.currency} />
        </div>
        <hr className="border-border" />
        <div className="flex justify-between items-baseline">
          <span className="font-heading text-h3 text-charcoal">Total</span>
          <Price amount={order.total} currency={order.currency} size="lg" />
        </div>
      </Reveal>

      {order.shippingAddress !== null ? (
        <Reveal as="section" className="mt-8">
          <h2 className="font-heading text-h3 text-charcoal mb-2">Shipping address</h2>
          <address className="not-italic font-body text-body-sm text-text-secondary leading-relaxed">
            {[order.shippingAddress.firstName, order.shippingAddress.lastName]
              .filter(Boolean)
              .join(' ')}
            <br />
            {order.shippingAddress.address1}
            <br />
            {order.shippingAddress.address2 !== null ? (
              <>
                {order.shippingAddress.address2}
                <br />
              </>
            ) : null}
            {order.shippingAddress.city}, {order.shippingAddress.province}{' '}
            {order.shippingAddress.zip}
            <br />
            {order.shippingAddress.country}
          </address>
        </Reveal>
      ) : null}

      {order.trackingNumber !== null ? (
        <Reveal as="section" className="mt-8">
          <h2 className="font-heading text-h3 text-charcoal mb-2">Tracking</h2>
          {order.trackingUrl !== null ? (
            <a
              href={order.trackingUrl}
              target="_blank"
              rel="noreferrer"
              className="text-forest underline-offset-2 hover:underline font-body text-body-sm"
            >
              {order.trackingNumber}
            </a>
          ) : (
            <p className="font-body text-body-sm text-text-secondary">{order.trackingNumber}</p>
          )}
        </Reveal>
      ) : null}

      {returnOpen ? (
        <RequestReturnModal
          open={returnOpen}
          orderNumber={order.orderNumber}
          onClose={() => setReturnOpen(false)}
          onSubmit={async (reason) => {
            const res = await fileReturn(reason)
            if (res.ok) reload()
            return res
          }}
        />
      ) : null}
    </>
  )
}
